import React, { useEffect, useRef, useState } from "react";
import { useDocumentTitle } from "../../hooks/customDocumentTitleHook";
import { useLocation, useNavigate } from "react-router-dom";
import { calculateAgeFromDOB, exchangeDate, Gender, getPractice, getSpeciality, usPhoneFormat } from "../../helpers";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { fetchSingleReferralData } from "../../services/easyReferral";
import { Config } from "../../Config";

export default function ReferralPrint() {
  let location_id = getPractice("location_id");
  let account_id = getPractice("account_id");
  let specialty_id = getSpeciality()?.id || 32;

  const [referralPatientData, setReferralPatientData] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [filteredConditions, setFilteredConditions] = useState([]);
  const navigate = useNavigate();
  const contentRef = useRef();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const referralId = queryParams.get('referral_id');

  useEffect(() => {
    if(referralId !== null){
      getReferralData(account_id, location_id, referralId);
    } else {
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account_id, location_id, specialty_id, referralId]);

  const getReferralData = async ( accountId, locationId, specialty_id, referralId ) => {
    const response = await fetchSingleReferralData(accountId, locationId, specialty_id,referralId);
    if (response) {
      if(response?.data === null) {
        window.close();
      }
      setReferralPatientData(response?.data);
      if (
        response?.data?.patient_info?.chronic_conditions &&
        response?.data?.patient_info?.chronic_conditions?.length > 0
      ) {
        handleFilterConditions(response?.data?.patient_info?.chronic_conditions);
      }
      setIsDataLoaded(true);
    }
  };

  const handleFilterConditions = (chronicConditions) => {
    const filteredData = chronicConditions?.reduce((acc, condition) => {
      const diseaseOrName = condition?.disease_name || condition?.name || "";
  
      if (
        !acc.some(
          (c) =>
            c?.disease_name?.includes("Type 2 Diabetes") ||
            c?.name?.includes("Type 2 Diabetes")
        ) ||
        !diseaseOrName.includes("Type 2 Diabetes")
      ) {
        acc.push({
          ...condition,
          name: diseaseOrName.includes("Type 2 Diabetes")
            ? "Type 2 Diabetes"
            : diseaseOrName,
        });
      }
  
      return acc;
    }, []);
  
    setFilteredConditions(filteredData);
  }; 

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (isDataLoaded) {
      // handlePrint();
    }
  }, [isDataLoaded]);

  const handleCloseWindow = () => {
    window.close();
  };


  const downloadPdf = () => {
    if (isDataLoaded) {
      var patient = referralPatientData && referralPatientData.patient_info?.last_name && referralPatientData.patient_info?.first_name ? `${referralPatientData.patient_info?.last_name} ${referralPatientData.patient_info?.first_name}` : "";
      const content = contentRef.current;
      html2canvas(content).then(canvas => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = pdfWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
        heightLeft -= pdfHeight;

        while (heightLeft >= 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pdfHeight;
        }

        pdf.save(`${patient} Referral.pdf`);
      });
    }
  };

  useDocumentTitle("Referral Patient");

  return (
    <div ref={contentRef} className="docpro_signout_wrap w-full">
      <div className="docpro_signout">
        <div className="docpro_signout_header" style={{ borderBottom: 0 }}>
          <div className="docpro_signout_logo">
            <img src="/images/logo.svg" alt="Logo" />
          </div>
          <h2 className="docpro_signout_title">
            Easy Referral
          </h2>
        </div>
        <div className="docpro_signout_top">
          <div className="docpro_grid docpro_grid_gap_20_16 docpro_grid_col_2 flex">
            <div className="docpro_grid_item w-full">
              <h6>Referral From</h6>
              {referralPatientData?.provider_info?.from && (
                <>
                  <p className="docpro_mb_0">
                    {referralPatientData?.provider_info?.from?.provider_organization_name || ""}
                  </p>
                  <p className="docpro_mb_0"><b>
                    {referralPatientData?.provider_info?.from?.authorized_official_first_name || ""} {referralPatientData?.provider_info?.from?.authorized_official_middle_name || ""} {referralPatientData?.provider_info?.from?.authorized_official_last_name || ""}
                  </b></p>
                  <p className="docpro_mb_0">
                    {referralPatientData?.provider_info?.from?.provider_address || ""}
                  </p>
                  <p>
                    Tel: {referralPatientData?.provider_info?.from?.provider_business_practice_location_address_telephone_number ? usPhoneFormat(referralPatientData?.provider_info?.from?.provider_business_practice_location_address_telephone_number) : ""} 
                    {referralPatientData?.provider_info?.from?.provider_business_practice_location_address_fax_number && (
                      <>
                        <span style={{ color: '#c1c1c1' }}> / </span>
                        Fax: {usPhoneFormat(referralPatientData?.provider_info?.from?.provider_business_practice_location_address_fax_number)}
                      </>
                    )}
                  </p>
                </>
              )}
            </div>

            <div className="docpro_grid_item w-full">
              <h6>Referral To</h6>
              <p className="docpro_mb_0">
                {referralPatientData?.provider_info?.to?.provider_organization_name || ""}
              </p>
              <p className="docpro_mb_0"><b>
                {referralPatientData?.provider_info?.to?.authorized_official_first_name || ""} {referralPatientData?.provider_info?.to?.authorized_official_middle_name || ""} {referralPatientData?.provider_info?.to?.authorized_official_last_name || ""}
              </b></p>
              <p className="docpro_mb_0">
                {referralPatientData?.provider_info?.to?.provider_address || ""}
              </p>
              <p>
                Tel: {referralPatientData?.provider_info?.to?.provider_business_practice_location_address_telephone_number ? usPhoneFormat(referralPatientData?.provider_info?.to?.provider_business_practice_location_address_telephone_number) : ""} 
                {referralPatientData?.provider_info?.to?.provider_business_practice_location_address_fax_number && (
                  <>
                    <span style={{ color: '#c1c1c1' }}> / </span>
                    Fax: {usPhoneFormat(referralPatientData?.provider_info?.to?.provider_business_practice_location_address_fax_number)}
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="py-2 mb-2">
            <hr />
          </div>

          <div>
            <h6>Patient Information</h6>
            <table className="docpro_referral_table">
              <tbody>
                <tr>
                  <td style={{ width: '150px' }}>Patient Name</td>
                  <td style={{ width: '20px' }}>:</td>
                  <td>{referralPatientData?.patient_info?.last_name} {referralPatientData?.patient_info?.first_name}</td>
                </tr>
                <tr>
                  <td>Patient DOB</td>
                  <td>:</td>
                  <td>{exchangeDate(referralPatientData?.patient_info?.dob)} ({referralPatientData?.patient_info?.dob ? calculateAgeFromDOB(referralPatientData?.patient_info?.dob) : 0}Y)</td>
                </tr>
                <tr>
                  <td>Patient Gender</td>
                  <td>:</td>
                  <td>{Gender(referralPatientData?.patient_info?.sex)}</td>
                </tr>
                <tr>
                  <td>Patient Phone</td>
                  <td>:</td>
                  <td>{usPhoneFormat(referralPatientData?.patient_info?.mobile_phone)}</td>
                </tr>
                <tr>
                  <td>Insurance Name</td>
                  <td>:</td>
                  <td>{referralPatientData?.patient_info?.insurance?.name}</td>
                </tr>
                <tr>
                  <td>Insurance ID</td>
                  <td>:</td>
                  <td>{referralPatientData?.patient_info?.insurance_code}</td>
                </tr>
                <tr>
                  <td>Chronic Conditions</td>
                  <td>:</td>
                  <td>
                    {filteredConditions && filteredConditions.length > 0 && (
                      <div className="label_wrapper">
                        <label className="docpro_fw_400 docpro_primary_color docpro_lh_18 docpro_grid_gap_4">
                          {filteredConditions.map((item, index) => (
                            <span key={item.id}>
                              {item.name}
                              {index !== filteredConditions.length - 1 && ", "}
                            </span>
                          ))}
                        </label>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            <h6>Reason For Referral</h6>
            <table className="docpro_referral_table">
              <tbody>
                <tr>
                  <td style={{ width: '150px' }}>Priority</td>
                  <td style={{ width: '20px' }}>:</td>
                  <td>{referralPatientData?.referral_info?.patient_conditon}</td>
                </tr>
                <tr>
                  <td>Date of Service</td>
                  <td>:</td>
                  <td>{exchangeDate(referralPatientData?.referral_info?.dos)}</td>
                </tr>
                <tr>
                  <td>Referral Date</td>
                  <td>:</td>
                  <td>{exchangeDate(referralPatientData?.referral_info?.refer_date)}</td>
                </tr>
                <tr>
                  <td>End Date</td>
                  <td>:</td>
                  <td>{exchangeDate(referralPatientData?.referral_info?.refer_end_date)}</td>
                </tr>
                <tr>
                  <td>Referral Reason</td>
                  <td>:</td>
                  <td>
                    <p>{referralPatientData?.referral_info?.refer_reason}</p>
                  </td>
                </tr>
                <tr>
                  <td>Diagnosis</td>
                  <td>:</td>
                  <td>
                    <p>{referralPatientData?.referral_info?.diagnosis}</p>
                  </td>
                </tr>
                <tr>
                  <td>Active Medication List</td>
                  <td>:</td>
                  <td>
                    <p>{referralPatientData?.referral_info?.active_medication_list}</p>
                  </td>
                </tr>
                <tr>
                  <td>Auth Code</td>
                  <td>:</td>
                  <td>{referralPatientData?.referral_info?.refer_auth_code}</td>
                </tr>
                <tr>
                  <td>Referral Status</td>
                  <td>:</td>
                  <td>{referralPatientData?.referral_info?.status}</td>
                </tr>
                <tr>
                  <td>Medical Summary</td>
                  <td>:</td>
                  <td>
                    <ul className="docpro_mb_0">
                      {referralPatientData?.referral_info?.attach_medical_summary_files?.map((element, index) => (
                        <li key={index}>
                          <a href={`${Config?.FileUploadPortal}${element?.file_path}`} target="_blank" rel="noopener noreferrer">
                            {Config?.FileUploadPortal} {element?.file_path}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>Consult Note</td>
                  <td>:</td>
                  <td>
                    <ul className="docpro_mb_0">
                      {referralPatientData?.referral_info?.refer_consult_note_files?.map((element, index) => (
                        <li key={index}>
                          <a href={`${Config?.FileUploadPortal}${element?.file_path}`} target="_blank" rel="noopener noreferrer">
                            {Config?.FileUploadPortal} {element?.file_path}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td>Lab Document</td>
                  <td>:</td>
                  <td>
                    <ul className="docpro_mb_0">
                      {referralPatientData?.referral_info?.refer_lab_document_files?.map((element, index) => (
                        <li key={index}>
                          <a href={`${Config?.FileUploadPortal}${element?.file_path}`} target="_blank" rel="noopener noreferrer">
                            {Config?.FileUploadPortal} {element?.file_path}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>

            <h6>Your Signature :</h6>
          </div>
        </div>
      </div>
      
      <div className="docpro_invoice_btns docpro_hide_print">
        <button
          className="docpro_invoice_btn docpro_color3 close_button"
          onClick={handleCloseWindow}
        >
          <span className="docpro_btn_icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
          </span>
          <span className="docpro_btn_text">Close</span>
        </button>

        <button
          className="docpro_invoice_btn docpro_color1"
          onClick={handlePrint}
        >
          <span className="docpro_btn_icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ionicon"
              viewBox="0 0 512 512"
            >
              <path
                d="M384 368h24a40.12 40.12 0 0040-40V168a40.12 40.12 0 00-40-40H104a40.12 40.12 0 00-40 40v160a40.12 40.12 0 0040 40h24"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <rect
                x={128}
                y={240}
                width={256}
                height={208}
                rx="24.32"
                ry="24.32"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <path
                d="M384 128v-24a40.12 40.12 0 00-40-40H168a40.12 40.12 0 00-40 40v24"
                fill="none"
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={32}
              />
              <circle cx={392} cy={184} r={24} fill="currentColor" />
            </svg>
          </span>
          <span className="docpro_btn_text">Print</span>
        </button>
        <button
          id="docpro_download_btn"
          className="docpro_invoice_btn docpro_color2"
          onClick={downloadPdf}
        >
          <span className="docpro_btn_icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="ionicon"
              viewBox="0 0 512 512"
            >
              <path
                d="M320 336h76c55 0 100-21.21 100-75.6s-53-73.47-96-75.6C391.11 99.74 329 48 256 48c-69 0-113.44 45.79-128 91.2-60 5.7-112 35.88-112 98.4S70 336 136 336h56M192 400.1l64 63.9 64-63.9M256 224v224.03"
                fill="none"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={32}
              />
            </svg>
          </span>
          <span className="docpro_btn_text">Download</span>
        </button>
        
      </div>
    </div>
  );
}
